import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Globe2, Percent, RefreshCw, WalletCards } from 'lucide-react';
import TopBar from '../components/TopBar';
import authService from '../services/authService';
import { fetchPowerBIFabricSqlData, fetchPowerBISemanticData } from '../services/powerbiService';
import '../modules/webv2/webV2Module.css';

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(String(value).replace(/,/g, '').replace(/\s/g, '').replace(/[$€£]/g, ''));
  return Number.isFinite(amount) ? amount : null;
};

const formatCurrency = (value) => {
  const amount = parseNumericValue(value);
  if (amount === null) return 'N/A';
  if (Math.abs(amount) >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${(numericValue * 100).toFixed(1)}%` : 'N/A';
};

const getNumericValue = (value) => {
  return parseNumericValue(value) || 0;
};

const unwrapBudgetPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { rows: [], meta: null };
  }

  const body = payload.data && typeof payload.data === 'object' ? payload.data : payload;

  return {
    rows: Array.isArray(body?.rows) ? body.rows : [],
    meta: body
  };
};

const getGaviTransactions = (row) => (Array.isArray(row?.gavi_transactions) ? row.gavi_transactions : []);

const getGaviTotalAmount = (row) => {
  const total = parseNumericValue(row?.gavi_total);
  if (total !== null) return total;

  return getGaviTransactions(row).reduce((sum, transaction) => sum + (parseNumericValue(transaction?.amount) || 0), 0);
};

const getGaviPriorityLabel = (transaction) => String(transaction?.priority_description || '').trim() || 'Unspecified';

const compareGaviTransactions = (a, b) => {
  const priorityDiff = getGaviPriorityLabel(a).localeCompare(getGaviPriorityLabel(b));
  if (priorityDiff !== 0) return priorityDiff;

  const yearDiff = Number(b.year || 0) - Number(a.year || 0);
  if (yearDiff !== 0) return yearDiff;

  return String(a.transaction_type || '').localeCompare(String(b.transaction_type || ''));
};

const getGaviPriorityGroupKey = (countryKey, label) => `${countryKey}::${label}`;

const getGaviGroupTotal = (transactions) => (
  transactions.reduce((sum, transaction) => sum + (parseNumericValue(transaction?.amount) || 0), 0)
);

const getWeightedShare = (rows) => {
  const immunizationTotal = rows.reduce((sum, row) => sum + getNumericValue(row.total_immunization), 0);
  const healthTotal = rows.reduce((sum, row) => sum + getNumericValue(row.total_health), 0);
  return healthTotal > 0 ? immunizationTotal / healthTotal : null;
};

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;

const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'hef-request'}-${index}`;
const getBudgetRowKey = (row) => `${String(row.country_iso3 || '').trim().toUpperCase()}-${row.year || 'unknown'}`;

const isLikelyIso3Label = (label, iso3) => {
  const normalized = String(label || '').trim().toUpperCase();
  return !normalized || normalized === iso3 || /^[A-Z]{3}$/.test(normalized);
};

const registerCountryName = (map, iso3, name) => {
  const code = String(iso3 || '').trim().toUpperCase();
  const label = String(name || '').trim();
  if (!code || !label) return;

  const current = map.get(code);
  const labelIsCode = isLikelyIso3Label(label, code);
  const currentIsCode = isLikelyIso3Label(current, code);

  if (!current || (currentIsCode && !labelIsCode)) {
    map.set(code, label);
    return;
  }

  if (!labelIsCode && !currentIsCode && label.length > current.length) {
    map.set(code, label);
  }
};

const getCountryDisplayName = (row, countryNameByIso3) => {
  const iso3 = String(row?.country_iso3 || row?.iso3 || '').trim().toUpperCase();
  const country = String(row?.country || '').trim();
  const fromGeo = iso3 ? countryNameByIso3.get(iso3) : null;

  if (fromGeo && !isLikelyIso3Label(fromGeo, iso3)) return fromGeo;
  if (country && !isLikelyIso3Label(country, iso3)) return country;
  return fromGeo || country || iso3 || 'Unknown country';
};

const normalizeCountryMatchValue = (value) => String(value || '').trim().toLowerCase();

const buildSelectedCountryMatchValues = ({
  selectedCountryKey,
  countryNameByIso3,
  selectedCountryRow,
  budgetRows
}) => {
  const values = new Set();
  const iso3 = String(selectedCountryKey || '').trim().toUpperCase();
  if (!iso3) return values;

  values.add(iso3.toLowerCase());

  const fromGeo = countryNameByIso3.get(iso3);
  if (fromGeo) values.add(normalizeCountryMatchValue(fromGeo));

  if (selectedCountryRow) {
    values.add(normalizeCountryMatchValue(getCountryDisplayName(selectedCountryRow, countryNameByIso3)));
    values.add(normalizeCountryMatchValue(selectedCountryRow.country));
  }

  budgetRows.forEach((row) => {
    if (String(row.country_iso3 || '').trim().toUpperCase() !== iso3) return;
    values.add(normalizeCountryMatchValue(row.country));
    values.add(normalizeCountryMatchValue(getCountryDisplayName(row, countryNameByIso3)));
  });

  values.delete('');
  return values;
};

const taRowMatchesSelectedCountry = (row, matchValues, taCountryColumnKey, taIso3ColumnKey) => {
  if (!matchValues.size) return false;

  const candidates = [
    getRowText(row, taCountryColumnKey),
    getRowText(row, taIso3ColumnKey)
  ]
    .map(normalizeCountryMatchValue)
    .filter(Boolean);

  return candidates.some((value) => matchValues.has(value));
};

const GAVI_FINANCE_YEAR_COUNT = 5;
const GAVI_FINANCE_UPDATE_COLUMNS = [
  { match: 'the total vaccine budget', label: 'Total vaccine budget', format: 'currency' },
  { match: 'the guaranteed budget', label: 'Guaranteed vaccine budget', format: 'currency' },
  { match: 'the discretionary budget', label: 'Discretionary vaccine budget', format: 'currency' },
  { match: 'total cash budget for 2026 2030', label: 'Total cash budget 2026–2030', format: 'currency' },
  { match: 'cso guardrail', label: 'CSO guardrail', format: 'currency' },
  { match: 'cce guardrail', label: 'CCE guardrail', format: 'currency' },
  { match: 'amount contingent on guaranteed vaccine budget introductions', label: 'Amount contingent on guaranteed vaccine introductions', format: 'text' },
  { match: 'country foundations total allocation', label: 'Country foundations', format: 'currency' },
  { match: 'expected co financing obligation', label: 'Co-financing obligations 2026–2030', format: 'currency' },
  { match: 'vaccines already introduced guaranteed', label: 'Vaccines already introduced, guaranteed', format: 'text' },
  { match: 'vaccines not yet introduced guaranteed', label: 'Vaccines not yet introduced, guaranteed', format: 'text' },
  { match: 'vaccines already introduced discretionary', label: 'Vaccines already introduced, discretionary', format: 'text' },
  { match: 'vaccines not yet introduced discretionary', label: 'Vaccines not yet introduced, discretionary', format: 'text' },
  { match: 'application preparation date range', label: 'Application preparation', format: 'text' },
  { match: 'submission to gavi date', label: 'Submission to Gavi', format: 'text' },
  { match: 'irc review starts date', label: 'IRC review starts', format: 'text' },
  { match: 'irc review ends date', label: 'IRC review ends', format: 'text' },
  { match: 'estimated grant start date', label: 'Estimated grant start', format: 'text' }
];

const LETTER_COUNTRY_ALIASES = {
  drc: 'democratic republic of the congo',
  'dr congo': 'democratic republic of the congo',
  'democratic republic of congo': 'democratic republic of the congo',
  'congo dem rep': 'democratic republic of the congo',
  'congo democratic republic of the': 'democratic republic of the congo',
  'congo republic': 'republic of the congo',
  'republic of congo': 'republic of the congo',
  congo: 'republic of the congo',
  'cote divoire': 'cote divoire',
  'cote d ivoire': 'cote divoire',
  'ivory coast': 'cote divoire',
  'the gambia': 'gambia',
  'viet nam': 'vietnam',
  'lao pdr': 'laos',
  'lao peoples democratic republic': 'laos',
  'united republic of tanzania': 'tanzania',
  'syrian arab republic': 'syria',
  'bolivia plurinational state of': 'bolivia',
  'iran islamic republic of': 'iran',
  'republic of moldova': 'moldova',
  swaziland: 'eswatini',
  macedonia: 'north macedonia',
  'former yugoslav republic of macedonia': 'north macedonia',
  burma: 'myanmar',
  'cape verde': 'cabo verde',
  'east timor': 'timor leste',
  'kyrgyz republic': 'kyrgyzstan',
  'state of palestine': 'palestine',
  'occupied palestinian territory': 'palestine',
  'congo brazzaville': 'republic of the congo',
  car: 'central african republic',
  png: 'papua new guinea'
};

const normalizeLetterText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const normalizeLetterCountryName = (value) => {
  const normalized = normalizeLetterText(value);
  return LETTER_COUNTRY_ALIASES[normalized] || normalized;
};

const letterPhraseInText = (text, phrase) => {
  if (!text || !phrase) return false;
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?:^| )${escaped}(?: |$)`).test(text);
};

const getLetterColumnValue = (row, names) => {
  const wanted = new Set(names.map((name) => name.toLowerCase()));
  const key = Object.keys(row || {}).find((column) => wanted.has(String(column).trim().toLowerCase()));
  return key ? String(row[key] ?? '').trim() : '';
};

const isPdfGaviLetter = (row) => {
  const extension = getLetterColumnValue(row, ['extension', 'file extension', 'file_extension']).replace(/^\./, '').toLowerCase();
  const name = getLetterColumnValue(row, ['name', 'file name', 'filename', 'fileleafref']);
  return extension === 'pdf' || /\.pdf$/i.test(name);
};

const getLetterSearchPhrases = (matchValues) => {
  const phrases = new Set();

  [...matchValues].forEach((value) => {
    const canonical = normalizeLetterCountryName(value);
    if (!canonical || canonical.length < 4) return;
    phrases.add(canonical);
    Object.entries(LETTER_COUNTRY_ALIASES).forEach(([alias, target]) => {
      if (target === canonical) phrases.add(alias);
    });
  });

  return [...phrases];
};

const financeRowMatchesCountry = (countryValue, matchValues) => {
  const country = normalizeLetterCountryName(countryValue);
  if (!country || !matchValues.size) return false;
  return [...matchValues].some((value) => normalizeLetterCountryName(value) === country);
};

const findFinanceColumnKey = (columns, match) => {
  const wanted = normalizeLetterText(match);
  if (!wanted) return null;
  return columns.find((column) => {
    const normalized = normalizeLetterText(column);
    return normalized === wanted || normalized.startsWith(wanted);
  }) || null;
};

const formatFinanceFieldValue = (value, format) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!text || text === '-' || text === '—') return 'N/A';
  if (format === 'currency') {
    const amount = parseNumericValue(text);
    return amount === null ? text : formatCurrency(amount);
  }
  return text;
};

const letterMatchesCountry = (fileName, matchValues, knownCountryPhrases = []) => {
  const text = normalizeLetterText(String(fileName || '').replace(/\.pdf$/i, ''));
  const phrases = getLetterSearchPhrases(matchValues);
  const matched = phrases.filter((phrase) => letterPhraseInText(text, phrase));
  if (!matched.length) return false;

  const belongsToLongerCountry = knownCountryPhrases.some((phrase) => (
    !phrases.includes(phrase)
    && letterPhraseInText(text, phrase)
    && matched.some((ownPhrase) => phrase.length > ownPhrase.length && phrase.includes(ownPhrase))
  ));

  return !belongsToLongerCountry;
};

const buildGaviLetterUrl = (folderPath, fileName) => {
  const folder = String(folderPath || '').trim();
  const name = String(fileName || '').trim();
  if (!/^https?:\/\//i.test(folder)) return '';

  const trimmedFolder = folder.replace(/[?#].*$/, '').replace(/\/+$/, '');
  let decodedFolder = trimmedFolder;
  try {
    decodedFolder = decodeURIComponent(trimmedFolder);
  } catch {
    decodedFolder = trimmedFolder;
  }

  if (name && decodedFolder.toLowerCase().endsWith(name.toLowerCase())) return folder;
  if (!name) return folder;
  return `${trimmedFolder}/${encodeURIComponent(name)}`;
};

const formatLetterDate = (value) => {
  const match = String(value || '').match(/\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : '';
};

const formatDetailLabel = (key) => String(key || '')
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDetailValue = (value, key = '') => {
  if (value === null || value === undefined || value === '') return '';
  if (String(key).toLowerCase().includes('amount')) return formatCurrency(value);
  if (Array.isArray(value)) return value.map((item) => formatDetailValue(item)).filter(Boolean).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getDetailEntries = (detail) => (
  Object.entries(detail || {})
    .map(([key, value]) => [key, formatDetailValue(value, key)])
    .filter(([, value]) => value)
);

const getBudgetTrend = (countryRows, metricKey, year) => {
  const series = countryRows
    .map((row) => ({
      year: Number(row.year),
      value: parseNumericValue(row[metricKey])
    }))
    .filter((item) => Number.isFinite(item.year) && item.value !== null)
    .sort((a, b) => a.year - b.year);

  const currentIndex = series.findIndex((item) => String(item.year) === String(year));
  if (currentIndex <= 0) {
    return {
      status: 'insufficient',
      label: 'N/A',
      detail: 'Trend cannot be calculated because no previous year is available for this country.'
    };
  }

  const previous = series[currentIndex - 1];
  const current = series[currentIndex];
  const difference = current.value - previous.value;
  const pctChange = previous.value !== 0 ? Math.abs(difference / previous.value) : null;
  const detail = [
    `Previous (${previous.year}): ${formatCurrency(previous.value)}`,
    `Current (${current.year}): ${formatCurrency(current.value)}`,
    `Change: ${difference >= 0 ? '+' : ''}${formatCurrency(difference)}`,
    pctChange !== null ? `Percent change: ${difference >= 0 ? '+' : '-'}${formatPercent(pctChange)}` : 'Percent change: N/A because previous value is zero'
  ].join(' | ');

  if (Math.abs(difference) < 1) {
    return { status: 'stable', label: 'Stable', detail };
  }

  const label = `${difference > 0 ? 'Increasing' : 'Decreasing'}${pctChange !== null ? ` ${formatPercent(pctChange)}` : ''}`;

  return {
    status: difference > 0 ? 'increasing' : 'decreasing',
    label,
    detail: `${previous.year} to ${current.year}`
  };
};

const getTaFocus = (row) => {
  const share = Number(row?.pct_immunization_within_health);
  const immunizationBudget = parseNumericValue(row?.total_immunization);
  const healthBudget = parseNumericValue(row?.total_health);

  if (immunizationBudget === null || healthBudget === null) {
    return {
      priority: 'Data validation',
      support: 'Review missing budget values and confirm source classification.'
    };
  }

  if (Number.isFinite(share) && share < 0.05) {
    return {
      priority: 'Budget advocacy',
      support: 'Support costing narrative and resource allocation discussion.'
    };
  }

  if (Number.isFinite(share) && share < 0.1) {
    return {
      priority: 'Targeted planning',
      support: 'Review immunization line items against programme priorities.'
    };
  }

  return {
    priority: 'Routine monitoring',
    support: 'Track execution risks and maintain budget visibility.'
  };
};

const BudgetContent = () => {
  const [budgetRows, setBudgetRows] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [countryTypeFilter, setCountryTypeFilter] = useState('gavi');
  const [showAllBudgetCountries, setShowAllBudgetCountries] = useState(false);
  const [expandedBudgetDetailKeys, setExpandedBudgetDetailKeys] = useState({});
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});
  const [expandedGaviPriorityGroups, setExpandedGaviPriorityGroups] = useState({});
  const [geoAdminCountries, setGeoAdminCountries] = useState([]);
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
  const taDatasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const taWorkspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const taDaxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const taTableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';
  const gaviFinanceTableName = process.env.REACT_APP_GAVI_FINANCE_UPDATE_TABLE_NAME || 'GaviFinanceUpdate';
  const gaviLettersTableName = process.env.REACT_APP_GAVI_LETTERS_TABLE_NAME || 'GaviLetters';
  const [gaviSemanticRows, setGaviSemanticRows] = useState([]);
  const [gaviSemanticColumns, setGaviSemanticColumns] = useState([]);
  const [isGaviSemanticLoading, setIsGaviSemanticLoading] = useState(true);
  const [gaviSemanticError, setGaviSemanticError] = useState('');
  const [gaviLetterRows, setGaviLetterRows] = useState([]);
  const [isGaviLettersLoading, setIsGaviLettersLoading] = useState(true);
  const [gaviLettersError, setGaviLettersError] = useState('');

  const loadBudgets = useCallback(async ({ refresh = false, shouldUpdate = () => true } = {}) => {
    if (refresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
      setError('');

      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${apiBase}/gibd-budgets${refresh ? '?refresh=true' : ''}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`
          },
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Budget request failed (${response.status})`);
        }

        const payload = await response.json();
        const { rows, meta } = unwrapBudgetPayload(payload);

        if (!shouldUpdate()) return;
        setBudgetRows(rows);
        setMetadata(meta);
      } catch (loadError) {
        if (!shouldUpdate()) return;
        if (!refresh) {
          setBudgetRows([]);
          setMetadata(null);
        }
        setError(refresh ? 'Budget refresh failed. The existing cached data is still shown.' : 'Budget data is unavailable right now.');
      } finally {
        if (!shouldUpdate()) return;
        if (refresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
  }, [apiBase]);

  useEffect(() => {
    let mounted = true;

    loadBudgets({ shouldUpdate: () => mounted });

    return () => {
      mounted = false;
    };
  }, [loadBudgets]);

  const loadGeoAdminCountryNames = useCallback(async () => {
    try {
      const idToken = await authService.getIdToken();
      const response = await fetch(`${apiBase}/db-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          query: `
            SELECT iso3, Country_Name, Gavi_Country
            FROM geo_admin
            WHERE iso3 IS NOT NULL AND TRIM(iso3) <> ''
          `
        }),
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`geo_admin lookup failed (${response.status})`);
      }

      const payload = await response.json();
      return Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
    } catch {
      return [];
    }
  }, [apiBase]);

  useEffect(() => {
    let mounted = true;

    loadGeoAdminCountryNames()
      .then((countries) => {
        if (mounted) setGeoAdminCountries(countries);
      });

    return () => {
      mounted = false;
    };
  }, [loadGeoAdminCountryNames]);

  useEffect(() => {
    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const effectiveQuery = String(taDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(taTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          taDatasetId,
          taWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [taDatasetId, taWorkspaceId, taDaxQuery, taTableName]);

  useEffect(() => {
    let mounted = true;

    const loadGaviSemanticData = async () => {
      setIsGaviSemanticLoading(true);
      setGaviSemanticError('');

      try {
        const data = await fetchPowerBIFabricSqlData({
          tableName: gaviFinanceTableName,
          maxRows: 5000
        });

        if (!mounted) return;
        setGaviSemanticRows(Array.isArray(data?.data) ? data.data : []);
        setGaviSemanticColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setGaviSemanticRows([]);
        setGaviSemanticColumns([]);
        setGaviSemanticError('Gavi country financing data is unavailable right now.');
      } finally {
        if (mounted) setIsGaviSemanticLoading(false);
      }
    };

    loadGaviSemanticData();

    return () => {
      mounted = false;
    };
  }, [gaviFinanceTableName]);

  useEffect(() => {
    let mounted = true;

    const loadGaviLetters = async () => {
      setIsGaviLettersLoading(true);
      setGaviLettersError('');

      try {
        const data = await fetchPowerBIFabricSqlData({
          tableName: gaviLettersTableName,
          maxRows: 5000
        });

        if (!mounted) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setGaviLetterRows(rows.filter(isPdfGaviLetter));
      } catch (loadError) {
        if (!mounted) return;
        setGaviLetterRows([]);
        setGaviLettersError('Gavi country letters are unavailable right now.');
      } finally {
        if (mounted) setIsGaviLettersLoading(false);
      }
    };

    loadGaviLetters();

    return () => {
      mounted = false;
    };
  }, [gaviLettersTableName]);

  const countryNameByIso3 = useMemo(() => {
    const map = new Map();

    geoAdminCountries.forEach((entry) => registerCountryName(map, entry.iso3, entry.Country_Name));
    budgetRows.forEach((row) => registerCountryName(map, row.country_iso3, row.country));
    (metadata?.gaviCountries || []).forEach((entry) => registerCountryName(map, entry.iso3, entry.country));

    return map;
  }, [budgetRows, geoAdminCountries, metadata]);

  const gaviCountryByIso3 = useMemo(() => {
    const map = new Map();

    geoAdminCountries.forEach((entry) => {
      const iso3 = String(entry?.iso3 || '').trim().toUpperCase();
      if (!iso3) return;
      map.set(iso3, String(entry?.Gavi_Country || '').trim().toLowerCase() === 'yes');
    });

    return map;
  }, [geoAdminCountries]);

  const availableYears = useMemo(() => (
    Array.from(new Set(budgetRows.map((row) => String(row.year).trim()).filter(Boolean)))
      .sort((a, b) => Number(b) - Number(a))
  ), [budgetRows]);

  useEffect(() => {
    if (availableYears.length === 0) return;
    if (selectedYear && availableYears.includes(selectedYear)) return;
    const defaultYear = '2024';
    setSelectedYear(availableYears.includes(defaultYear) ? defaultYear : String(availableYears[0]));
  }, [availableYears, selectedYear]);

  const selectedYearRows = useMemo(() => (
    selectedYear
      ? budgetRows.filter((row) => String(row.year) === String(selectedYear))
      : budgetRows
  ), [budgetRows, selectedYear]);

  const filteredCountryRows = useMemo(() => (
    selectedYearRows
      .filter((row) => {
        const iso3 = String(row.country_iso3 || '').trim().toUpperCase();
        const isGaviCountry = gaviCountryByIso3.get(iso3) === true;
        if (countryTypeFilter === 'gavi') return isGaviCountry;
        if (countryTypeFilter === 'non-gavi') return !isGaviCountry;
        return true;
      })
      .sort((a, b) => (
      getCountryDisplayName(a, countryNameByIso3).localeCompare(getCountryDisplayName(b, countryNameByIso3))
      ))
  ), [countryNameByIso3, countryTypeFilter, gaviCountryByIso3, selectedYearRows]);
  const budgetTableRows = showAllBudgetCountries ? filteredCountryRows : filteredCountryRows.slice(0, 8);

  useEffect(() => {
    setShowAllBudgetCountries(false);
  }, [countryTypeFilter, selectedYear]);

  const countryOptions = useMemo(() => {
    const optionsByIso3 = new Map();

    filteredCountryRows.forEach((row) => {
      const key = String(row.country_iso3 || '').trim().toUpperCase();
      if (!key || optionsByIso3.has(key)) return;

      optionsByIso3.set(key, {
        key,
        label: getCountryDisplayName(row, countryNameByIso3)
      });
    });

    return Array.from(optionsByIso3.values());
  }, [filteredCountryRows, countryNameByIso3]);

  useEffect(() => {
    if (countryOptions.length === 0) {
      if (selectedCountryKey) setSelectedCountryKey('');
      return;
    }

    if (!countryOptions.some((option) => option.key === selectedCountryKey)) {
      setSelectedCountryKey(countryOptions[0].key);
    }
  }, [countryOptions, selectedCountryKey]);

  const trendByCountryYear = useMemo(() => {
    const rowsByCountry = new Map();

    budgetRows.forEach((row) => {
      const iso3 = String(row.country_iso3 || '').trim().toUpperCase();
      if (!iso3) return;

      rowsByCountry.set(iso3, [...(rowsByCountry.get(iso3) || []), row]);
    });

    const trendMap = new Map();

    budgetRows.forEach((row) => {
      const iso3 = String(row.country_iso3 || '').trim().toUpperCase();
      if (!iso3) return;

      const countryRows = rowsByCountry.get(iso3) || [];
      trendMap.set(`${iso3}-${row.year}`, {
        health: getBudgetTrend(countryRows, 'total_health', row.year),
        immunization: getBudgetTrend(countryRows, 'total_immunization', row.year)
      });
    });

    return trendMap;
  }, [budgetRows]);

  const budgetKpis = useMemo(() => {
    const countries = new Set(selectedYearRows.map((row) => row.country_iso3).filter(Boolean)).size;
    const immunizationTotal = selectedYearRows.reduce((sum, row) => sum + getNumericValue(row.total_immunization), 0);
    const gaviTotal = selectedYearRows.reduce((sum, row) => sum + getGaviTotalAmount(row), 0);
    const weightedShare = getWeightedShare(selectedYearRows);

    return [
      {
        key: 'countries',
        icon: Globe2,
        value: countries.toLocaleString(),
        label: 'Countries with budget data',
        meta: selectedYear ? `Reporting year ${selectedYear}` : 'All reporting years'
      },
      {
        key: 'immunization',
        icon: WalletCards,
        value: formatCurrency(immunizationTotal),
        label: 'Total immunization budget',
        meta: 'LINE ITEM rows from GIBD'
      },
      {
        key: 'gavi',
        icon: WalletCards,
        value: formatCurrency(gaviTotal),
        label: 'Gavi financing',
        meta: 'Sum of Gavi finance transactions'
      },
      {
        key: 'share',
        icon: Percent,
        value: formatPercent(weightedShare),
        label: 'Immunization within health',
        meta: 'Weighted by total health budget'
      }
    ];
  }, [selectedYear, selectedYearRows]);

  const selectedCountryRow = useMemo(() => (
    filteredCountryRows.find((row) => String(row.country_iso3 || '').trim().toUpperCase() === selectedCountryKey) || null
  ), [filteredCountryRows, selectedCountryKey]);

  const selectedCountryHistory = useMemo(() => {
    if (!selectedCountryKey) return [];

    return budgetRows
      .filter((row) => String(row.country_iso3 || '').trim().toUpperCase() === selectedCountryKey)
      .sort((a, b) => Number(b.year || 0) - Number(a.year || 0));
  }, [budgetRows, selectedCountryKey]);

  const selectedCountryTrend = selectedCountryRow
    ? trendByCountryYear.get(`${String(selectedCountryRow.country_iso3 || '').trim().toUpperCase()}-${selectedCountryRow.year}`) || {}
    : {};
  const selectedCountryTaFocus = selectedCountryRow ? getTaFocus(selectedCountryRow) : null;
  const mergedGaviFromBudgetRows = useMemo(() => {
    if (!selectedCountryKey) return [];

    const transactions = [];

    budgetRows.forEach((row) => {
      if (String(row.country_iso3 || '').trim().toUpperCase() !== selectedCountryKey) return;

      getGaviTransactions(row).forEach((transaction) => {
        transactions.push({
          ...transaction,
          year: transaction.year || row.year
        });
      });
    });

    return transactions.sort(compareGaviTransactions);
  }, [budgetRows, selectedCountryKey]);

  const selectedCountryGaviTransactions = mergedGaviFromBudgetRows;

  const selectedCountryGaviTransactionGroups = useMemo(() => {
    const groups = [];
    const groupByLabel = new Map();

    selectedCountryGaviTransactions.forEach((transaction) => {
      const label = getGaviPriorityLabel(transaction);
      let group = groupByLabel.get(label);

      if (!group) {
        group = { label, transactions: [] };
        groupByLabel.set(label, group);
        groups.push(group);
      }

      group.transactions.push(transaction);
    });

    return groups;
  }, [selectedCountryGaviTransactions]);

  const selectedCountryGaviTransactionsForYear = useMemo(() => (
    selectedYear
      ? selectedCountryGaviTransactions.filter((transaction) => String(transaction.year) === String(selectedYear))
      : selectedCountryGaviTransactions
  ), [selectedCountryGaviTransactions, selectedYear]);

  const selectedCountryGaviTotal = selectedCountryGaviTransactionsForYear.reduce(
    (sum, transaction) => sum + (parseNumericValue(transaction?.amount) || 0),
    0
  );
  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taIso3ColumnKey = useMemo(
    () => findColumnKey(taColumns, ['iso3', 'country iso3', 'country_iso3', 'country code', 'country iso']),
    [taColumns]
  );
  const selectedCountryMatchValues = useMemo(() => (
    buildSelectedCountryMatchValues({
      selectedCountryKey,
      countryNameByIso3,
      selectedCountryRow,
      budgetRows
    })
  ), [selectedCountryKey, countryNameByIso3, selectedCountryRow, budgetRows]);
  const selectedCountryDisplayName = useMemo(() => (
    selectedCountryKey
      ? getCountryDisplayName({ country_iso3: selectedCountryKey }, countryNameByIso3)
      : ''
  ), [selectedCountryKey, countryNameByIso3]);
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'issue'
    ]),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const hefTaRequests = useMemo(() => {
    const hefRows = taRows.filter((row) => getRowText(row, taLeadCoeTeamColumnKey).toLowerCase() === 'hef');
    if (!selectedCountryKey) return [];

    return hefRows.filter((row) => (
      taRowMatchesSelectedCountry(row, selectedCountryMatchValues, taCountryColumnKey, taIso3ColumnKey)
    ));
  }, [
    taRows,
    taLeadCoeTeamColumnKey,
    selectedCountryKey,
    selectedCountryMatchValues,
    taCountryColumnKey,
    taIso3ColumnKey
  ]);

  const gaviSemanticCountryColumnKey = useMemo(
    () => findColumnKey(gaviSemanticColumns, ['country']),
    [gaviSemanticColumns]
  );
  const gaviSemanticNisBudgetColumnKeys = useMemo(
    () => [
      'the guaranteed budget',
      'the discretionary budget',
      'total cash budget for 2026 2030',
      'country foundations total allocation'
    ]
      .map((columnName) => findFinanceColumnKey(gaviSemanticColumns, columnName))
      .filter(Boolean),
    [gaviSemanticColumns]
  );
  const gaviSemanticCoFinancingColumnKey = useMemo(
    () => findFinanceColumnKey(gaviSemanticColumns, 'expected co financing obligation'),
    [gaviSemanticColumns]
  );
  const nisBudgetYearlyAverageByCountry = useMemo(() => {
    const valuesByCountry = new Map();
    const coFinancingValuesByCountry = new Map();

    gaviSemanticRows.forEach((row) => {
      const country = normalizeLetterCountryName(getRowText(row, gaviSemanticCountryColumnKey));
      const totalNisBudget = gaviSemanticNisBudgetColumnKeys.reduce(
        (sum, columnKey) => sum + (parseNumericValue(getRowText(row, columnKey)) || 0),
        0
      );
      const coFinancing = parseNumericValue(getRowText(row, gaviSemanticCoFinancingColumnKey));
      const yearCount = GAVI_FINANCE_YEAR_COUNT;
      if (!country || !yearCount) return;

      if (totalNisBudget > 0) {
        valuesByCountry.set(country, [...(valuesByCountry.get(country) || []), totalNisBudget / yearCount]);
      }
      if (coFinancing !== null) {
        coFinancingValuesByCountry.set(country, [...(coFinancingValuesByCountry.get(country) || []), coFinancing / yearCount]);
      }
    });

    const averagesByCountry = new Map();
    valuesByCountry.forEach((values, country) => {
      const average = values.reduce((sum, value) => sum + value, 0) / values.length;
      averagesByCountry.set(country, { nisBudgetYearlyAverage: average, coFinancingYearlyAverage: null });
    });
    coFinancingValuesByCountry.forEach((values, country) => {
      const average = values.reduce((sum, value) => sum + value, 0) / values.length;
      averagesByCountry.set(country, {
        ...(averagesByCountry.get(country) || {}),
        coFinancingYearlyAverage: average
      });
    });

    return averagesByCountry;
  }, [
    gaviSemanticCoFinancingColumnKey,
    gaviSemanticCountryColumnKey,
    gaviSemanticNisBudgetColumnKeys,
    gaviSemanticRows
  ]);
  const gaviSemanticTableColumns = useMemo(() => {
    const usedColumnKeys = new Set();

    return GAVI_FINANCE_UPDATE_COLUMNS
      .map((column) => {
        const columnKey = findFinanceColumnKey(gaviSemanticColumns, column.match);
        if (!columnKey || usedColumnKeys.has(columnKey)) return null;
        usedColumnKeys.add(columnKey);
        return { ...column, columnKey };
      })
      .filter(Boolean);
  }, [gaviSemanticColumns]);
  const knownLetterCountryPhrases = useMemo(() => {
    const phrases = new Set();

    countryNameByIso3.forEach((name) => {
      const canonical = normalizeLetterCountryName(name);
      if (canonical && canonical.length >= 4) phrases.add(canonical);
    });

    Object.entries(LETTER_COUNTRY_ALIASES).forEach(([alias, target]) => {
      if (phrases.has(target)) phrases.add(alias);
    });

    return [...phrases];
  }, [countryNameByIso3]);
  const gaviSemanticCountryRows = useMemo(() => {
    if (!selectedCountryKey) return [];

    return gaviSemanticRows.filter((row) => (
      financeRowMatchesCountry(getRowText(row, gaviSemanticCountryColumnKey), selectedCountryMatchValues)
    ));
  }, [
    gaviSemanticRows,
    selectedCountryKey,
    selectedCountryMatchValues,
    gaviSemanticCountryColumnKey
  ]);
  const selectedCountryLetters = useMemo(() => {
    if (!selectedCountryKey) return [];

    return gaviLetterRows
      .map((row, index) => {
        const name = getLetterColumnValue(row, ['name', 'file name', 'filename', 'fileleafref']);
        const folderPath = getLetterColumnValue(row, ['folder path', 'folderpath', 'folder_path', 'path']);
        const modified = formatLetterDate(getLetterColumnValue(row, [
          'date modified',
          'datemodified',
          'date_modified',
          'modified',
          'date created',
          'datecreated'
        ]));

        return {
          key: `${name}-${folderPath}-${index}`,
          name,
          modified,
          url: buildGaviLetterUrl(folderPath, name)
        };
      })
      .filter((letter) => letter.name && letterMatchesCountry(letter.name, selectedCountryMatchValues, knownLetterCountryPhrases))
      .sort((a, b) => a.name.localeCompare(b.name) || a.modified.localeCompare(b.modified));
  }, [gaviLetterRows, knownLetterCountryPhrases, selectedCountryKey, selectedCountryMatchValues]);

  const refreshedDate = metadata?.refreshedAt
    ? new Date(metadata.refreshedAt).toLocaleString()
    : null;

  const handleRefresh = () => {
    loadBudgets({ refresh: true });
  };

  const toggleBudgetDetailExpanded = (budgetRowKey) => {
    setExpandedBudgetDetailKeys((current) => ({
      ...current,
      [budgetRowKey]: !current[budgetRowKey]
    }));
  };

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  const toggleGaviPriorityGroup = (groupKey) => {
    setExpandedGaviPriorityGroups((current) => ({
      ...current,
      [groupKey]: !current[groupKey]
    }));
  };

  useEffect(() => {
    setExpandedGaviPriorityGroups({});
    setExpandedTaRequestKeys({});
  }, [selectedCountryKey]);

  return (
    <section className="home-section vaccines-page-section budgets-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <p className="vaccines-section-label">GIBD Budget Analysis</p>
          <h2 className="vaccines-dashboard-title">Immunization budget overview</h2>
          <p className="vaccines-dashboard-subtitle">
            Track total health budgets, immunization line items, Gavi financing, and the share of immunization within health budgets by country. GIBD data from <a href="https://gibd.unicef.org" target="_blank" rel="noopener noreferrer">https://gibd.unicef.org</a>
          </p>
        </div>
        <div className="budgets-toolbar" aria-label="Budget filters">
          <button
            type="button"
            className="budgets-refresh-button"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
          >
            <RefreshCw className={isRefreshing ? 'is-spinning' : ''} />
            {isRefreshing ? 'Refreshing...' : 'Refresh budget data'}
          </button>
          <label>
            Year
            <select value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)}>
              {availableYears.map((year) => (
                <option key={`year-${year}`} value={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="vaccines-kpi-row budgets-kpi-row">
        {budgetKpis.map((item) => {
          const Icon = item.icon;

          return (
            <article key={item.key} className={`vaccines-kpi-card budgets-kpi-${item.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">{isLoading ? '...' : item.value}</p>
                <p className="vaccines-kpi-label">{item.label}</p>
                <p className="vaccines-kpi-meta">{item.meta}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="vaccines-schedule-card budgets-country-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Country Data</p>
            <h3 className="vaccines-card-title">Health and immunization budgets</h3>
          </div>
          <div className="vaccines-matrix-actions">
            <div className="vaccines-segmented-control" aria-label="Country funding filter">
              <button
                type="button"
                className={countryTypeFilter === 'gavi' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('gavi')}
              >
                Gavi Countries
              </button>
              <button
                type="button"
                className={countryTypeFilter === 'non-gavi' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('non-gavi')}
              >
                Non-Gavi Countries
              </button>
              <button
                type="button"
                className={countryTypeFilter === 'all' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('all')}
              >
                All
              </button>
            </div>
            <p className="budgets-data-note">
              {isLoading
                ? 'Loading GIBD data...'
                : refreshedDate
                  ? `Updated ${refreshedDate}`
                  : `${filteredCountryRows.length} rows`}
            </p>
          </div>
        </div>

        {error && <p className="budgets-error-note">{error}</p>}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-country-table" aria-label="Country budget data">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Year</th>
                <th scope="col">Total health</th>
                <th scope="col">Total immunization</th>
                <th scope="col">NIS Budget Yearly Avg.</th>
                <th scope="col">Avg Country Co-financing</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="6">Loading budget data...</td>
                </tr>
              )}
              {!isLoading && !error && filteredCountryRows.length === 0 && (
                <tr>
                  <td colSpan="6">No budget rows found for this view.</td>
                </tr>
              )}
              {!isLoading && budgetTableRows.map((row) => {
                const budgetRowKey = getBudgetRowKey(row);
                const countryDisplayName = getCountryDisplayName(row, countryNameByIso3);
                const nisBudgetAverages = nisBudgetYearlyAverageByCountry.get(normalizeLetterCountryName(countryDisplayName)) ||
                  nisBudgetYearlyAverageByCountry.get(normalizeLetterCountryName(row.country));

                const countryKey = String(row.country_iso3 || '').trim().toUpperCase();

                return (
                  <tr
                    key={budgetRowKey}
                    className={countryKey === selectedCountryKey ? 'is-selected' : ''}
                    aria-selected={countryKey === selectedCountryKey}
                  >
                    <th scope="row">
                      <button type="button" onClick={() => setSelectedCountryKey(countryKey)}>
                        {countryDisplayName}
                      </button>
                    </th>
                    <td>{row.year || 'N/A'}</td>
                    <td>{formatCurrency(row.total_health)}</td>
                    <td>{formatCurrency(row.total_immunization)}</td>
                    <td>{formatCurrency(nisBudgetAverages?.nisBudgetYearlyAverage)}</td>
                    <td>{formatCurrency(nisBudgetAverages?.coFinancingYearlyAverage)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredCountryRows.length > 8 && (
          <div className="vaccines-matrix-footer">
            <p className="vaccines-footnote">
              Showing {budgetTableRows.length} of {filteredCountryRows.length} countries in this view.
            </p>
            <button
              type="button"
              className="vaccines-expand-button"
              onClick={() => setShowAllBudgetCountries((current) => !current)}
            >
              {showAllBudgetCountries ? 'Show fewer countries' : `Show all ${filteredCountryRows.length} countries`}
            </button>
          </div>
        )}
      </div>

      <div className="vaccines-schedule-card budgets-snapshot-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Country Snapshots</p>
            <h3 className="vaccines-card-title">Selected country budget details</h3>
          </div>
        </div>

        {isLoading && <p className="budgets-data-note">Loading country snapshot...</p>}
        {!isLoading && !selectedCountryKey && (
          <p className="budgets-data-note">Click a country in the table above to view details.</p>
        )}

        {!isLoading && selectedCountryKey && !selectedCountryRow && (
          <p className="budgets-data-note">
            No GIBD budget row for {selectedCountryKey} in {selectedYear || 'this year'}. Gavi finance data is still shown below.
          </p>
        )}

        {!isLoading && selectedCountryRow && (
          <div className="budgets-snapshot-grid">
              <div>
                <span>Country</span>
                <strong>{getCountryDisplayName(selectedCountryRow, countryNameByIso3)}</strong>
                <small>{selectedCountryRow.country_iso3 || 'N/A'} | {selectedCountryRow.year || 'N/A'}</small>
              </div>
              <div>
                <span>Total health</span>
                <strong>{formatCurrency(selectedCountryRow.total_health)}</strong>
                <small>All health budget rows</small>
              </div>
              <div>
                <span>Total immunization</span>
                <strong>{formatCurrency(selectedCountryRow.total_immunization)}</strong>
                <small>Immunization line items</small>
              </div>
              <div>
                <span>Immunization share</span>
                <strong>{formatPercent(selectedCountryRow.pct_immunization_within_health)}</strong>
                <small>Within total health budget</small>
              </div>
              <div>
                <span>Total Gavi financing</span>
                <strong>{formatCurrency(selectedCountryGaviTotal)}</strong>
                <small>{selectedCountryGaviTransactionsForYear.length} transaction{selectedCountryGaviTransactionsForYear.length === 1 ? '' : 's'} in {selectedYear || 'N/A'}</small>
              </div>
              <div>
                <span>Health budget trend</span>
                <strong>
                  <span className={`budgets-trend-pill is-${selectedCountryTrend.health?.status || 'insufficient'}`}>
                    {selectedCountryTrend.health?.label || 'N/A'}
                  </span>
                </strong>
                <small>{selectedCountryTrend.health?.detail || 'No previous year available'}</small>
              </div>
              <div>
                <span>Immunization budget trend</span>
                <strong>
                  <span className={`budgets-trend-pill is-${selectedCountryTrend.immunization?.status || 'insufficient'}`}>
                    {selectedCountryTrend.immunization?.label || 'N/A'}
                  </span>
                </strong>
                <small>{selectedCountryTrend.immunization?.detail || 'No previous year available'}</small>
              </div>
              <div>
                <span>TA focus</span>
                <strong>
                  <span className="budgets-ta-priority">{selectedCountryTaFocus?.priority || 'N/A'}</span>
                </strong>
                <small>{selectedCountryTaFocus?.support || 'No support signal available'}</small>
              </div>
          </div>
        )}

        <div className="budgets-country-detail-tables">
            <div className="vaccines-card-header budgets-gavi-header">
              <div>
                <h4 className="vaccines-card-title">NIS GAVI 6.0 Application</h4>
              </div>
              <p className="budgets-data-note">
                {isGaviSemanticLoading
                  ? 'Loading NIS GAVI 6.0 application data...'
                  : !selectedCountryKey
                    ? 'Select a country in the table above'
                    : gaviSemanticError
                      ? gaviSemanticError
                      : gaviSemanticCountryRows.length > 0
                        ? `Application data for ${selectedCountryDisplayName || selectedCountryKey}`
                        : `No NIS GAVI 6.0 application record for ${selectedCountryDisplayName || selectedCountryKey}`}
              </p>
            </div>

            <div className="budgets-gavi-country-section">
              {!selectedCountryKey && !isGaviSemanticLoading && (
                <p className="budgets-data-note">Click a country in the table above to view NIS GAVI 6.0 application data.</p>
              )}
              {selectedCountryKey && (
              <div className="vaccines-wuenic-table-wrap budgets-table-wrap budgets-gavi-country-wrap">
                <table className="vaccines-wuenic-table budgets-gavi-country-detail-table" aria-label="NIS GAVI 6.0 Application">
                  <thead>
                    <tr>
                      <th scope="col">Field</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isGaviSemanticLoading && (
                      <tr>
                        <td colSpan="2">Loading Gavi country data...</td>
                      </tr>
                    )}
                    {!isGaviSemanticLoading && gaviSemanticError && (
                      <tr>
                        <td colSpan="2">{gaviSemanticError}</td>
                      </tr>
                    )}
                    {!isGaviSemanticLoading && !gaviSemanticError && gaviSemanticCountryRows.length === 0 && (
                      <tr>
                        <td colSpan="2">
                          No NIS GAVI 6.0 application record found for {selectedCountryDisplayName || selectedCountryKey}.
                        </td>
                      </tr>
                    )}
                    {!isGaviSemanticLoading && !gaviSemanticError && gaviSemanticCountryRows.map((row, rowIndex) => (
                      gaviSemanticTableColumns.map((column) => {
                        const value = formatFinanceFieldValue(getRowText(row, column.columnKey), column.format);

                        return (
                          <tr key={`gavi-semantic-${rowIndex}-${column.columnKey}`}>
                            <th scope="row">{column.label}</th>
                            <td>{value}</td>
                          </tr>
                        );
                      })
                    ))}
                    <tr>
                      <th scope="row">Country communication letters</th>
                      <td>
                        {isGaviLettersLoading && 'Loading letters...'}
                        {!isGaviLettersLoading && gaviLettersError && gaviLettersError}
                        {!isGaviLettersLoading && !gaviLettersError && selectedCountryLetters.length === 0 && 'No PDF letters for this country.'}
                        {!isGaviLettersLoading && !gaviLettersError && selectedCountryLetters.length > 0 && (
                          <ul className="budgets-gavi-letter-list">
                            {selectedCountryLetters.map((letter) => (
                              <li key={letter.key}>
                                {letter.url ? (
                                  <a href={letter.url} target="_blank" rel="noopener noreferrer">
                                    {letter.name}
                                  </a>
                                ) : letter.name}
                                {letter.modified ? ` (${letter.modified})` : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
            </div>

        {!isLoading && selectedCountryKey && (
          <>
            <div className="vaccines-card-header budgets-gavi-header">
              <div>
                <h5 className="vaccines-card-title">GAVI Financial support</h5>
              </div>
              <p className="budgets-data-note">
                {metadata?.gaviQueryFailed
                  ? 'Gavi data could not be loaded from the database.'
                  : selectedCountryGaviTransactionsForYear.length > 0
                    ? `${selectedCountryGaviTransactionsForYear.length} in ${selectedYear || 'selected year'} · ${selectedCountryGaviTransactions.length} total for country`
                    : selectedCountryGaviTransactions.length > 0
                      ? `No transactions in ${selectedYear || 'selected year'} · ${selectedCountryGaviTransactions.length} in other years`
                      : metadata?.totalGaviRows > 0
                        ? `No Gavi rows for ${selectedCountryKey} in loaded data (${metadata.totalGaviRows} rows total).`
                        : `No Gavi transactions for ${selectedCountryKey}`}
              </p>
            </div>

            <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
              <table className="vaccines-wuenic-table budgets-table budgets-gavi-table" aria-label="GAVI Financial support">
                <thead>
                  <tr>
                    <th scope="col">Year</th>
                    <th scope="col">Transaction type</th>
                    <th scope="col">Applicant</th>
                    <th scope="col">Implementor</th>
                    <th scope="col">Funds center</th>
                    <th scope="col">Amount (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCountryGaviTransactions.length === 0 && (
                    <tr>
                      <td colSpan="6">No Gavi finance data for this country.</td>
                    </tr>
                  )}
                  {selectedCountryGaviTransactionGroups.map((group) => {
                    const groupKey = getGaviPriorityGroupKey(selectedCountryKey, group.label);
                    const isExpanded = Boolean(expandedGaviPriorityGroups[groupKey]);
                    const groupTotal = getGaviGroupTotal(group.transactions);
                    const transactionLabel = group.transactions.length === 1 ? 'transaction' : 'transactions';

                    return (
                      <Fragment key={`${selectedCountryKey}-gavi-priority-${group.label}`}>
                        <tr className="budgets-gavi-group-header">
                          <th scope="rowgroup" colSpan="6">
                            <button
                              type="button"
                              className="budgets-gavi-group-toggle"
                              onClick={() => toggleGaviPriorityGroup(groupKey)}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? (
                                <ChevronDown className="budgets-gavi-group-chevron" aria-hidden="true" />
                              ) : (
                                <ChevronRight className="budgets-gavi-group-chevron" aria-hidden="true" />
                              )}
                              <span className="budgets-gavi-group-label">{group.label}</span>
                              <span className="budgets-gavi-group-meta">
                                {group.transactions.length} {transactionLabel} · {formatCurrency(groupTotal)}
                              </span>
                            </button>
                          </th>
                        </tr>
                        {isExpanded && group.transactions.map((transaction, index) => (
                          <tr
                            key={`${selectedCountryKey}-gavi-${group.label}-${transaction.year}-${index}`}
                            className={selectedYear && String(transaction.year) !== String(selectedYear) ? 'budgets-gavi-row-muted' : ''}
                          >
                            <td>{transaction.year || 'N/A'}</td>
                            <td>{transaction.transaction_type || 'N/A'}</td>
                            <td>{transaction.applicant || 'N/A'}</td>
                            <td>{transaction.implementor || 'N/A'}</td>
                            <td>{transaction.funds_center_code ?? 'N/A'}</td>
                            <td>{formatCurrency(transaction.amount)}</td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {selectedCountryHistory.length > 0 && (
            <div className="budgets-country-history-section">
            <div className="vaccines-card-header budgets-gavi-header">
              <div>
                <h4 className="vaccines-card-title">Country Budgets (GIBD) and GAVI Support</h4>
              </div>
            </div>
            <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
              <table className="vaccines-wuenic-table budgets-snapshot-history-table" aria-label="Country Budgets (GIBD) and GAVI Support">
                <thead>
                  <tr>
                    <th scope="col">Year</th>
                    <th scope="col">Total health</th>
                    <th scope="col">Total immunization</th>
                    <th scope="col">Gavi financing</th>
                    <th scope="col">Immunization share</th>
                    <th scope="col">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCountryHistory.map((row) => {
                    const budgetRowKey = getBudgetRowKey(row);
                    const budgetDetails = Array.isArray(row.budget_details) ? row.budget_details : [];
                    const isExpanded = Boolean(expandedBudgetDetailKeys[budgetRowKey]);

                    return (
                      <Fragment key={`${budgetRowKey}-snapshot`}>
                        <tr>
                          <td>{row.year || 'N/A'}</td>
                          <td>{formatCurrency(row.total_health)}</td>
                          <td>{formatCurrency(row.total_immunization)}</td>
                          <td>{formatCurrency(getGaviTotalAmount(row))}</td>
                          <td>{formatPercent(row.pct_immunization_within_health)}</td>
                          <td>
                            {budgetDetails.length > 0 ? (
                              <button
                                type="button"
                                className="budgets-ta-read-more-button"
                                onClick={() => toggleBudgetDetailExpanded(budgetRowKey)}
                              >
                                {isExpanded ? 'Hide details' : `View details (${budgetDetails.length})`}
                              </button>
                            ) : 'N/A'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="budgets-detail-row">
                            <td colSpan="6">
                              <div className="budgets-detail-panel">
                                {budgetDetails.map((detail, detailIndex) => (
                                  <div key={`${budgetRowKey}-detail-${detailIndex}`} className="budgets-detail-card">
                                    <div className="budgets-detail-card-title">
                                      <span>{getRowText(detail, 'rowType') || `Budget line ${detailIndex + 1}`}</span>
                                      <strong>{formatCurrency(detail?.amountInUSD)}</strong>
                                    </div>
                                    <dl>
                                      {getDetailEntries(detail).map(([key, value]) => (
                                        <Fragment key={`${budgetRowKey}-${detailIndex}-${key}`}>
                                          <dt>{formatDetailLabel(key)}</dt>
                                          <dd>{value}</dd>
                                        </Fragment>
                                      ))}
                                    </dl>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </div>
            )}
              </>
            )}
        </div>
      </div>

      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">HEF TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : !selectedCountryKey
                ? 'Select a country in the table above'
                : `${hefTaRequests.length} request${hefTaRequests.length === 1 ? '' : 's'} for ${selectedCountryDisplayName}`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}
        {!isTaLoading && !taError && !selectedCountryKey && (
          <p className="budgets-data-note">Click a country in the table above to view HEF TA requests for that country.</p>
        )}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="HEF TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading HEF TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && selectedCountryKey && hefTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No HEF TA requests found for {selectedCountryDisplayName}.</td>
                </tr>
              )}
              {!isTaLoading && hefTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || 'HEF'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const Budgets = ({ userClaims, isEmbedded = false }) => {
  if (isEmbedded) {
    return <BudgetContent />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="budgets" userClaims={userClaims} />
      <div className="v2-module">
        <div className="p-4">
          <BudgetContent />
        </div>
      </div>
    </div>
  );
};

export default Budgets;
